import { capitalize, groupBy, lowerCase, partition, sum } from 'lodash';
import simpleSoilAttribute, {
  SimpleSoilAttribute,
  SimpleSoilAttributeItem,
  SimpleSoilAttributeProps,
} from './simpleSoilAttribute';
import { asDecimal } from '../../utils/formatters';
import { GetItemsData } from '../../types/soilAttributes';

interface FuzzyLogicSoilAttributeProps extends SimpleSoilAttributeProps {
  fuzzyLogicRuleName: string;
  matchRating?: boolean;
}

const borderStyle = { borderRight: '1px solid var(--border-color)' };

const simpleColAccessors = (soilAttribute: SimpleSoilAttribute) =>
  soilAttribute.cols.map(({ accessor }) => accessor);

const getCols = (soilAttribute: SimpleSoilAttribute) => {
  const [nameCol, ratingCol, ...remainingCols] = soilAttribute.cols;
  return [
    nameCol,
    ratingCol,
    { displayString: 'Component name (%)', accessor: 'componentName' },
    { displayString: 'Rating reason (rating value)', accessor: 'ratingReason' },
    ...remainingCols,
  ];
};

const formatComponentName = (componentName: string, componentPercent: string) =>
  `${capitalize(lowerCase(componentName))} (${componentPercent}%)`;

const formatRatingReason = (ratingReason: string, ratingValue: string) => {
  const floatValue = parseFloat(ratingValue);
  if (Number.isNaN(floatValue)) {
    return ratingReason;
  }
  // Match WSS format
  if (ratingReason === 'Well suited' && floatValue === 0) return '';
  return `${ratingReason} (${asDecimal(floatValue)})`;
};

const getMapUnitItemsCount = (
  components: string[][],
  componentMap: { [component: string]: string[][] },
) =>
  sum(
    components.map((component) =>
      component[18] == null // Component is not rated
        ? components.length > 1
          ? 0 // If there are other components, skip this one
          : 1 // If this is the only component, display one item
        : // If no fuzzy logic items, display one item
          componentMap[component[10]]?.length || 1,
    ),
  );

const addRowSpan = (
  soilComponentIndex: number,
  includeComponentName: boolean,
  mapUnitItemsCount: number,
  componentItemsCount: number,
  colAccessors: string[],
) =>
  soilComponentIndex === 0 && includeComponentName
    ? // First soil component
      // Add row span for simple columns and componentName column
      {
        rowSpan: {
          ...Object.fromEntries(
            colAccessors.map((accessor) => [accessor, mapUnitItemsCount]),
          ),
          componentName: componentItemsCount,
        },
      }
    : includeComponentName
    ? // Subsequent soil components
      // Add row span for componentName column, but skip simple columns
      {
        rowSpan: { componentName: componentItemsCount },
        skipCols: colAccessors,
        style: borderStyle,
      }
    : // All other fuzzy logic items
      // Skip simple columns and componentName column
      {
        skipCols: [...colAccessors, 'componentName'],
        style: borderStyle,
      };

const getFuzzyLogicItems = (
  item: SimpleSoilAttributeItem,
  fuzzyLogicRows: string[][],
  mapUnitItemsCount: number,
  soilComponentIndex: number,
  colAccessors: string[],
) =>
  fuzzyLogicRows.flatMap((fuzzyLogicRow, i) => {
    // Include component name if at start of new soil component
    const includeComponentName = ['0', '1'].includes(fuzzyLogicRow[17]);
    return {
      ...item,
      key: `${item.mapUnitKey}:${soilComponentIndex}:${i}`,
      componentName: formatComponentName(fuzzyLogicRow[10], fuzzyLogicRow[9]),
      ratingReason: formatRatingReason(fuzzyLogicRow[19], fuzzyLogicRow[18]),
      ...addRowSpan(
        soilComponentIndex,
        includeComponentName,
        mapUnitItemsCount,
        fuzzyLogicRows.length,
        colAccessors,
      ),
    };
  });

const getSoilComponentFuzzyLogicItems = (
  componentMap: {
    [component: string]: string[][];
  },
  componentCount: number,
  item: SimpleSoilAttributeItem,
  mapUnitItemsCount: number,
  colAccessors: string[],
) => (soilComponentRow: string[], i: number) => {
  const notRated = soilComponentRow[18] == null;
  const fuzzyLogicRows = componentMap[soilComponentRow[10]];
  if (notRated) {
    if (componentCount > 1) return [];
    return [
      {
        ...item,
        key: `${item.mapUnitKey}:${i}`,
        componentName: formatComponentName(
          soilComponentRow[10],
          soilComponentRow[9],
        ),
        ratingReason: formatRatingReason(
          soilComponentRow[19],
          soilComponentRow[18],
        ),
      },
    ];
  }
  return getFuzzyLogicItems(
    item,
    // If no fuzzy logic rows, get item for the soil component row
    fuzzyLogicRows || [soilComponentRow],
    mapUnitItemsCount,
    i,
    colAccessors,
  );
};

const getItems = (soilAttribute: SimpleSoilAttribute, matchRating: boolean) => (
  data: GetItemsData,
) => {
  const { fuzzyLogicInterpretation } = data;
  // Group results by map unit key
  const fuzzyLogicMap = groupBy(
    fuzzyLogicInterpretation?.Table || [],
    (row) => row[8],
  );
  const items = soilAttribute.getItems(data);
  // For each simple map unit item for the soil attribute...
  return items.flatMap((item) => {
    // Get all fuzzy logic rows associated with the map unit
    const mapUnitFuzzyLogicRows = fuzzyLogicMap[item.mapUnitKey];
    if (!mapUnitFuzzyLogicRows.length) return [item];
    // Partition into rows with ruleDepth 0 and all other rows
    const [allSoilComponentRows, componentFuzzyLogicRows] = partition(
      mapUnitFuzzyLogicRows,
      (row) => row[16] === '0',
    );
    // Only display soil components with rating equal to the map unit rating
    // if matchRating specified
    const soilComponentRows = matchRating
      ? allSoilComponentRows.filter(
          (row) => row[19] === item.mapUnitRatingString,
        )
      : allSoilComponentRows;
    // Group other rows by their soil component within the map unit
    const componentMap = groupBy(componentFuzzyLogicRows, (row) => row[10]);
    // Count number of items for this map unit
    const mapUnitItemsCount = getMapUnitItemsCount(
      soilComponentRows,
      componentMap,
    );
    // Get fuzzy logic items for each soil component
    return soilComponentRows.flatMap(
      getSoilComponentFuzzyLogicItems(
        componentMap,
        soilComponentRows.length,
        item,
        mapUnitItemsCount,
        simpleColAccessors(soilAttribute),
      ),
    );
  });
};

const fuzzyLogicSoilAttribute = ({
  fuzzyLogicRuleName,
  matchRating = true,
  ...soilAttributeProps
}: FuzzyLogicSoilAttributeProps) => {
  const baseSoilAttribute = simpleSoilAttribute(soilAttributeProps);
  return {
    ...baseSoilAttribute,
    fuzzyLogicRuleName,
    cols: getCols(baseSoilAttribute),
    getItems: getItems(baseSoilAttribute, matchRating),
  };
};

export default fuzzyLogicSoilAttribute;
